import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { ModelLead } from '@app/api/Leads/types';
import { useGetSalesFunnelLeadsMutation } from '@app/api/SalesFunnels/api';
import {
  LEAD_CARD_PATH,
  LEADS_PATH,
  SALES_FUNNELS_LEADS_PATH,
  SALES_FUNNELS_LEAD_CARD_PATH,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useLeadsRegistryScopedFilters } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistryFilters';
import { useLeadsRegistryRealtime } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistryRealtime';
import { useLeadsRegistrySettings } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistrySettings';
import { buildLeadsRegistrySearchFilter } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/buildLeadsRegistrySearchFilter';
import { normalizeLeadsPortionSize } from '@app/pages/Leads/LeadsRegistryPage/utils';
import {
  patchLeadInList,
  useLatestRegistryResultBridge,
  useReplaceLatestRegistryLead,
} from '@app/pages/Leads/model';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { mergeLeads, parseSearchNumber } from '../utils';

import { useLeadCardRegistryListRestore } from './useLeadCardRegistryListRestore';

export const useLeadCardPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { filters: funnelFilters } = useLeadsRegistryScopedFilters('funnels');
  const { loadBatchSize } = useLeadsRegistrySettings('funnels');
  const [getSalesFunnelLeads] = useGetSalesFunnelLeadsMutation();
  const replaceRegistryLead = useReplaceLatestRegistryLead();
  const {
    hasNextPage,
    isLoading: isRegistryLoading,
    isNextPageLoading,
    leads: registryLeads,
    requestNextPage,
  } = useLatestRegistryResultBridge();
  const [funnelLeads, setFunnelLeads] = useState<ModelLead[]>([]);
  const [funnelTotalCount, setFunnelTotalCount] = useState(0);
  const [requestedMaxSize, setRequestedMaxSize] =
    useState<number>(loadBatchSize);
  const [isFunnelLoading, setIsFunnelLoading] = useState(false);
  const [isNextFunnelPageLoading, setIsNextFunnelPageLoading] = useState(false);
  const requestIdRef = useRef(0);
  const isFunnelLoadMoreRequestedRef = useRef(false);
  const isResettingPaginationRef = useRef(false);

  const leadId = useMemo(
    () => parseSearchNumber(searchParams.get('leadId')),
    [searchParams]
  );

  const funnelId = useMemo(
    () => parseSearchNumber(searchParams.get('funnelId')),
    [searchParams]
  );

  const stageId = useMemo(
    () => parseSearchNumber(searchParams.get('stageId')),
    [searchParams]
  );

  const isFunnelSource =
    searchParams.get('source') === 'funnels' ||
    location.pathname === SALES_FUNNELS_LEAD_CARD_PATH;
  const isRegistrySource = !isFunnelSource;

  const hasFunnelContext =
    isFunnelSource && funnelId !== null && stageId !== null;

  useLeadCardRegistryListRestore({ isEnabled: isRegistrySource });

  // Keep the shared registry search result live while the card is open from
  // the registry: the left lead list reads the same slice the registry patches.
  useLeadsRegistryRealtime({ isEnabled: isRegistrySource });

  const funnelSearchFilter = useMemo(
    () => ({
      ...buildLeadsRegistrySearchFilter({
        ...funnelFilters,
        salesFunnelId: funnelId,
        funnelStages: stageId === null ? [] : [stageId],
      }),
      MaxItems: normalizeLeadsPortionSize(requestedMaxSize),
    }),
    [funnelFilters, funnelId, requestedMaxSize, stageId]
  );

  const funnelSearchFilterResetKey = useMemo(
    () =>
      JSON.stringify({
        ...funnelSearchFilter,
        MaxItems: normalizeLeadsPortionSize(loadBatchSize),
      }),
    [funnelSearchFilter, loadBatchSize]
  );

  const buildFunnelLeadCardUrl = useCallback(
    (nextLeadId: number) => {
      if (funnelId === null || stageId === null) {
        return `${LEAD_CARD_PATH}?leadId=${nextLeadId}&source=registry`;
      }

      const nextSearchParams = new URLSearchParams({
        leadId: String(nextLeadId),
        source: 'funnels',
        funnelId: String(funnelId),
        stageId: String(stageId),
      });

      return `${SALES_FUNNELS_LEAD_CARD_PATH}?${nextSearchParams.toString()}`;
    },
    [funnelId, stageId]
  );

  const runFunnelSearch = useCallback(
    async (isNextPage: boolean) => {
      if (
        !ActiveBranchId ||
        !hasFunnelContext ||
        funnelId === null ||
        stageId === null
      ) {
        return;
      }

      const requestId = ++requestIdRef.current;

      if (isNextPage) {
        setIsNextFunnelPageLoading(true);
      } else {
        setIsFunnelLoading(true);
      }

      try {
        const response = await getSalesFunnelLeads({
          ActiveBranchId,
          data: {
            SalesFunnelId: funnelId,
            LeadsFilter: funnelSearchFilter,
          },
        }).unwrap();

        if (requestId !== requestIdRef.current) return;

        const responseStage =
          response.StageLeads?.find((stage) => stage.StageId === stageId) ??
          null;
        const nextLeads = responseStage?.Leads ?? [];

        setFunnelLeads((prevLeads) =>
          isNextPage ? mergeLeads(prevLeads, nextLeads) : nextLeads
        );
        setFunnelTotalCount(responseStage?.TotalLeads ?? 0);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          showNotification(error, 'warning');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsFunnelLoading(false);
          setIsNextFunnelPageLoading(false);
          isFunnelLoadMoreRequestedRef.current = false;
        }
      }
    },
    [
      ActiveBranchId,
      funnelId,
      funnelSearchFilter,
      getSalesFunnelLeads,
      hasFunnelContext,
      showNotification,
      stageId,
    ]
  );

  useEffect(() => {
    requestIdRef.current += 1;
    isResettingPaginationRef.current = true;
    setFunnelLeads([]);
    setFunnelTotalCount(0);
    setIsFunnelLoading(false);
    setIsNextFunnelPageLoading(false);
    isFunnelLoadMoreRequestedRef.current = false;
    setRequestedMaxSize(normalizeLeadsPortionSize(loadBatchSize));
  }, [funnelSearchFilterResetKey, loadBatchSize]);

  useEffect(() => {
    if (!hasFunnelContext) {
      requestIdRef.current += 1;
      setFunnelLeads([]);
      setFunnelTotalCount(0);
      setIsFunnelLoading(false);
      setIsNextFunnelPageLoading(false);
      isFunnelLoadMoreRequestedRef.current = false;
      return;
    }

    if (
      isResettingPaginationRef.current &&
      requestedMaxSize !== normalizeLeadsPortionSize(loadBatchSize)
    ) {
      return;
    }

    isResettingPaginationRef.current = false;
    void runFunnelSearch(
      requestedMaxSize > normalizeLeadsPortionSize(loadBatchSize)
    );
  }, [hasFunnelContext, loadBatchSize, requestedMaxSize, runFunnelSearch]);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const leadClickHandler = useCallback(
    (lead: ModelLead) => {
      if (hasFunnelContext) {
        navigate(buildFunnelLeadCardUrl(lead.Id));
        return;
      }

      navigate(`${LEAD_CARD_PATH}?leadId=${lead.Id}&source=registry`);
    },
    [buildFunnelLeadCardUrl, hasFunnelContext, navigate]
  );

  const leadUpdatedHandler = useCallback(
    (updatedLead: ModelLead) => {
      replaceRegistryLead(updatedLead);
      setFunnelLeads((currentLeads) =>
        patchLeadInList(currentLeads, updatedLead)
      );
    },
    [replaceRegistryLead]
  );

  const backToLeadsHandler = useCallback(() => {
    navigate(isFunnelSource ? SALES_FUNNELS_LEADS_PATH : LEADS_PATH);
  }, [isFunnelSource, navigate]);

  const loadNextLeadsHandler = useCallback(() => {
    if (isFunnelSource && !hasFunnelContext) return;

    if (hasFunnelContext) {
      if (isNextFunnelPageLoading || funnelLeads.length >= funnelTotalCount) {
        return;
      }

      if (isFunnelLoadMoreRequestedRef.current) {
        return;
      }

      isFunnelLoadMoreRequestedRef.current = true;
      setRequestedMaxSize(
        (value) =>
          normalizeLeadsPortionSize(value) +
          normalizeLeadsPortionSize(loadBatchSize)
      );
      return;
    }

    if (isNextPageLoading || !hasNextPage) return;

    requestNextPage();
  }, [
    funnelLeads.length,
    funnelTotalCount,
    hasFunnelContext,
    hasNextPage,
    isFunnelSource,
    isNextFunnelPageLoading,
    isNextPageLoading,
    loadBatchSize,
    requestNextPage,
  ]);

  const hasNextFunnelPage =
    hasFunnelContext && funnelLeads.length < funnelTotalCount;

  return {
    leadId,
    goBack,
    leads: hasFunnelContext
      ? funnelLeads
      : isRegistrySource
      ? registryLeads
      : [],
    hasNextLeadsPage: hasFunnelContext
      ? hasNextFunnelPage
      : isRegistrySource
      ? hasNextPage
      : false,
    onLeadClick: leadClickHandler,
    onLeadUpdated: leadUpdatedHandler,
    onBackToLeads: backToLeadsHandler,
    onLoadNextLeads: loadNextLeadsHandler,
    isLeadsLoading:
      (hasFunnelContext && isFunnelLoading && funnelLeads.length === 0) ||
      (isRegistrySource && isRegistryLoading && registryLeads.length === 0),
    isNextLeadsPageLoading: hasFunnelContext
      ? isNextFunnelPageLoading
      : isRegistrySource
      ? isNextPageLoading
      : false,
  };
};
